import {ColumnFactory, ColumnWidth, ControlRoomColumn} from "../../common/ui/table/ControlRoomColumns";
import {User} from "../../user/model";
import {FormattedMessage} from "react-intl";
import {Link} from "react-router-dom";

import * as React from "react";

import {Button} from "react-bootstrap";
import {LcdIcon} from "../../common/ui/icon/LcdIcon";

type ColumnEntry = ControlRoomColumn<User>;

const USER_CONTENT_MESSAGES: { [key: string]: ReactIntl.FormattedMessage.MessageDescriptor } = {
  deleteTooltip: {
    id: "studio.products.user-content-list.delete-user",
    defaultMessage: "Delete user",
  },
  toggleTooltip: {
    id: "studio.products.user-content-list.toggle-user",
    defaultMessage: "Toggle enabled/disabled",
  },
};

export class UserContentColumnFactory implements ColumnFactory<User> {
  private readonly removeItem: (user: User) => void;
  private readonly changeItem: (user: User) => void;
  private readonly canEdit: boolean;

  constructor(options: { canEdit: boolean; removeItem?: (user: User) => void, changeItem?: (user: User) => void }) {
    if (options.canEdit) {
      this.removeItem = options.removeItem;
      this.changeItem = options.changeItem;
    }
    this.canEdit = options.canEdit;
  }

  createColumn(columnID: string): ControlRoomColumn<User> {
    return this.columns[columnID];
  }

  getAvailableColumnIDs(): string[] {
    const availableColumnIDs = [];
    for (const prop in this.columns) {
      if (this.columns.hasOwnProperty(prop)) {
        if (prop === "delete" || prop === "toggle") {
          if (this.canEdit) {
            availableColumnIDs.push(prop);
          }
        } else {
          availableColumnIDs.push(prop);
        }
      }
    }
    return availableColumnIDs;
  }

  getInitialColumnIDs(): string[] {
    return this.getAvailableColumnIDs();
  }

  getInitialColumnWidth(columnID: string): ColumnWidth {
    return this.columns[columnID].columnWidth || ColumnWidth.pixels(200);
  }

  columns: { [columnID: string]: ColumnEntry } = {
    name: {
      header: <FormattedMessage id="studio.users-content-list.user-name" defaultMessage="User Name"/>,
      cellContent: (user) => {
        return <Link to={`/settings/userdetails/${encodeURIComponent(user.username)}`}>{user.username}</Link>;
      },
      cellTooltip: (user) => user.username,
      columnWidth: ColumnWidth.flex(2),
    },
    roles: {
      header: <FormattedMessage id="studio.users-content-list.roles" defaultMessage="Roles"/>,
      cellContent: (user) => {
        return user.roles.join(', ');
      },
      cellTooltip: () => "",
      columnWidth: ColumnWidth.flex(8),
    },
    toggle: {
      header: <FormattedMessage id="studio.users-content-list.enabled" defaultMessage="Enabled"/>,
      cellContent: (user) => {
        return (
            <div>
              <Button className="btn-icon"
                      style={{backgroundColor: "transparent", color: "currentColor", border: "1px solid transparent"}}
                      onClick={(event) => {
                        event.stopPropagation();
                        if (typeof this.removeItem === "function") {
                          this.changeItem(user);
                        }
                      }}
              >
                {user.enabled ?
                 <LcdIcon icon="check"/> :
                 <LcdIcon icon="unchecked"/>
                }
              </Button>
            </div>
        )
      },
      cellTooltip: (user, intl) => intl.formatMessage(USER_CONTENT_MESSAGES.toggleTooltip),
      columnWidth: ColumnWidth.pixels(100),
    },
    delete: {
      header: <FormattedMessage id="studio.users-content-list.delete" defaultMessage="Delete"/>,
      cellContent: (user) => {
        return (
            <div>
              <Button className="btn-icon"
                      style={{backgroundColor: "transparent", color: "currentColor", border: "1px solid transparent"}}
                      onClick={(event) => {
                        event.stopPropagation();
                        if (typeof this.removeItem === "function") {
                          this.removeItem(user);
                        }
                      }}
              >
                <LcdIcon icon="delete"/>
              </Button>
            </div>
        )
      },
      cellTooltip: (user, intl) => intl.formatMessage(USER_CONTENT_MESSAGES.deleteTooltip),
      columnWidth: ColumnWidth.pixels(100),
    },
  }
}